/**
 * Registry of BOV template assets, one entry per office-location / product-type /
 * sub-product-type combination.
 *
 * The artwork lives in S3, under four prefixes that mirror how it actually varies:
 *
 *   covers/       page 01 - per product type, and per tenancy for retail and office; six,
 *                 industrial having one cover for both its tenancies and multi-family one
 *   back-covers/  the last page - per office and product type, and per tenancy for retail
 *                 only; nine, multi-family having one for both offices
 *   cities/       one per office - houston, dallas
 *   shared/       the same in every deck - capital map, Global Reach page, wordmark
 *
 * Twenty files serve all fourteen combinations. `covers/industrial-multi-tenant.jpg` is still in
 * the S3 bucket but no longer referenced, having been replaced by the reissue below; delete it
 * there once the replacement is approved.
 *
 * These moved to S3 on 2026-08-20, were served from `public/img/bov-templates` for part of
 * 2026-08-28 while the covers and back covers were regraded to the gradient artwork, and went
 * back to the bucket the same day once the thirteen regraded files were uploaded. There is no
 * local copy any more. Nothing else in the app references this artwork, so **this constant is
 * the only thing that has to change to move it again**.
 *
 * Note this is the bucket root, not `arc.path.media` - that points at the `backup/` prefix,
 * a sibling of `bov-templates/`, so it cannot be reused here.
 */

import BOV_ZIP_OFFICE from '../../bov-zip-office';

const ASSET_ROOT = 'https://strivenext.s3.us-east-1.amazonaws.com/bov-templates';

/**
 * The STRIVE wordmark printed at the foot of every page. It is a transparent PNG with a
 * grey gradient, and the template uses the same asset on light and dark pages, so there
 * is no light/dark variant to choose between. Shared across all combinations.
 */
export const BOV_LOGO = `${ASSET_ROOT}/shared/strive-logo.png`;

/**
 * Artwork that is the same in every combination, so it is stored once under `shared/` rather
 * than copied into each folder. Both were verified byte-identical across all twelve before
 * being consolidated.
 *
 * The Global Reach page used to be the deck's one fully static page - one PNG carrying its
 * map, both panels, the legend and the STRIVE mark. It was rebuilt as markup on 2026-09-12
 * so the sheet could be changed without the designer, and all that survives of the artwork
 * is the world map below.
 */
const SHARED_CAPITAL_MAP = `${ASSET_ROOT}/shared/capital-map.png`;

/**
 * The world map on page 06, the only artwork left on that sheet. Shared by every deck, and
 * drawn at 618 x 370 page pixels.
 *
 * Note it sits at the bucket root rather than under `shared/` with the two above, which is
 * where it was uploaded on 2026-09-15. Nothing depends on the prefix beyond this line.
 */
export const GLOBAL_REACH_MAP = `${ASSET_ROOT}/global-reach-map.png`;

/**
 * The reissued industrial cover, drawn by both industrial decks.
 *
 * It replaced the single-tenant cover first, after the client spotted the same property printed
 * twice in that grid, and then the multi-tenant one, whose grid had the same two duplicates. The
 * supplied artwork already matched the page aspect, and the "01 / 11" baked into its foot was
 * painted out so it cannot contradict the number the deck prints.
 *
 * The filename still says single-tenant because that is the name it was uploaded under, and it is
 * the same artwork either way - the industrial grid was never per-tenancy. Renaming it in the
 * bucket to `covers/industrial.jpg` would be tidier; nothing here depends on the name.
 *
 * NOTE the artwork only resolved one of the two duplicate pairs the client marked. The grey
 * building that appeared in rows 1 and 3 of column 1 is gone, but the red and cream building with
 * the blue awning is still in both row 1 column 4 and row 3 column 2 - the same property from a
 * slightly tighter crop. That needs the designer, not code.
 */
const COVER_INDUSTRIAL = `${ASSET_ROOT}/covers/industrial-single-tenant.jpg`;

/**
 * Covers, keyed by product type and tenancy. They do **not** vary by office - Houston and
 * Dallas share each one - so there are five files rather than twelve. Verified byte-identical
 * per pair before consolidating.
 *
 * Because these covers are shared, the industrial reissue above reaches all four industrial
 * decks: Houston and Dallas, both tenancies.
 */
const COVER = {
    'retail/multi-tenant':      `${ASSET_ROOT}/covers/retail-multi-tenant.jpg`,
    'retail/single-tenant':     `${ASSET_ROOT}/covers/retail-single-tenant.jpg`,
    'office/multi-tenant':      `${ASSET_ROOT}/covers/office-multi-tenant.jpg`,
    'office/single-tenant':     `${ASSET_ROOT}/covers/office-single-tenant.jpg`,
    'industrial/multi-tenant':  COVER_INDUSTRIAL,
    'industrial/single-tenant': COVER_INDUSTRIAL,
    // Multi-Family has one cover for both offices and no tenancy - see the Multi-Family
    // entries below for why there is no single-tenant deck. Built from the Dallas reference's
    // page 01: its photo grid dropped between the retail cover's header and graded foot bar,
    // which sit at the same heights (the grid is y 173..1386 on both, to within a pixel).
    'apartments':               `${ASSET_ROOT}/covers/multi-family.jpg`,
};

/**
 * Where each cover's graded foot bar begins, in page pixels on the 816-tall page.
 *
 * The bar is painted into the artwork and does not start at the same height in all five
 * files - retail single-tenant's begins 9px lower than the rest. Page 01 writes on that bar,
 * so anything placed at one fixed y sits 9px further from the edge on one deck than on
 * another, which is what a fixed position produced: a comfortable gap on the office and
 * multi-tenant covers and almost none on retail single-tenant. Cover.js measures its own gap
 * down from these instead, so every deck prints the same one.
 *
 * Keyed by asset rather than by template because the bar belongs to the file: the two
 * industrial decks share one cover and therefore one figure.
 *
 * Measured off the assets themselves, not read off a design - each is the first row, scanning
 * up from the foot of the 2112 x 1632 original, at which the image stops being flat across
 * its width, converted to page pixels. Re-measure with
 * `scripts/measure-cover-foot-bar.ps1` whenever a cover is reissued; the industrial one
 * already has been once.
 */
export const COVER_FOOT_TOP = {
    [COVER['retail/multi-tenant']]:  693,
    [COVER['retail/single-tenant']]: 702,
    [COVER['office/multi-tenant']]:  693,
    [COVER['office/single-tenant']]: 693,
    [COVER_INDUSTRIAL]:              696,
    // The retail multi-tenant cover's foot bar, carried over unchanged.
    [COVER['apartments']]:           693,
};

/**
 * Used for a cover with no measurement of its own - a new asset added without one being
 * taken. It is the **lowest** bar top rather than the most common one on purpose: guessing
 * too low only prints the text further down its bar, while guessing too high prints it above
 * the bar and over the photographs.
 */
export const COVER_FOOT_TOP_FALLBACK = 702;

/**
 * The Texas Advantage skyline, one per office. The Dallas drawing is wider and shorter than
 * the Houston one: the template places both on the same left and bottom edge at the same
 * width and lets the height follow, so Dallas sits 16px shorter than the 191px in
 * PrintBOV.scss. The height belongs to the artwork, so it is named here once rather than
 * repeated in all six Dallas entries.
 */
const CITY_HOUSTON = `${ASSET_ROOT}/cities/houston.jpg`;
const CITY_DALLAS = `${ASSET_ROOT}/cities/dallas.jpg`;
const CITY_DALLAS_HEIGHT = 175;

/**
 * Back covers - the final page of each deck, supplied per office and product group. They end
 * in a contact strip: two agent cells, the office address, STRIVEre.com, divided by green
 * rules. The deal's real agents are drawn over the strip; see BackCover.
 *
 * Unlike the covers these **do** vary by office, so they are keyed office-first. Each asset is
 * cropped to the page aspect when built, because the supplied artwork is 4:3, and its two
 * "Agent Name / (123) 456-6789" placeholders are painted out so live names can replace them.
 *
 * Eight assets cover all twelve decks, because only retail was supplied per tenancy: its two
 * carry visibly different artwork, a multi-tenant centre and a single-tenant grid of national
 * brands. Office and industrial were each supplied once per office and serve both tenancies -
 * hence the keys without a tenancy segment.
 */
/**
 * These eight are the patched covers - the old address block and the middle rule taken out of
 * the strip so BackCover.js can draw them live. They replaced the originals in the bucket on
 * 2026-09-15.
 *
 * **A back cover reissued from the designer's own file will have that content baked in again**,
 * and the deck will then print the address twice, once from the JPG and once over it, with the
 * rule still between. Run `scripts/patch-back-cover-strip.ps1` over any replacement before it
 * goes to the bucket.
 */
const BACK_COVER_ROOT = `${ASSET_ROOT}/back-covers`;

const BACK_COVER = {
    'dallas/retail/multi-tenant':   `${BACK_COVER_ROOT}/dallas-retail-multi-tenant.jpg`,
    'dallas/retail/single-tenant':  `${BACK_COVER_ROOT}/dallas-retail-single-tenant.jpg`,
    'houston/retail/multi-tenant':  `${BACK_COVER_ROOT}/houston-retail-multi-tenant.jpg`,
    'houston/retail/single-tenant': `${BACK_COVER_ROOT}/houston-retail-single-tenant.jpg`,
    'dallas/office':                `${BACK_COVER_ROOT}/dallas-office.jpg`,
    'houston/office':               `${BACK_COVER_ROOT}/houston-office.jpg`,
    'dallas/industrial':            `${BACK_COVER_ROOT}/dallas-industrial.jpg`,
    'houston/industrial':           `${BACK_COVER_ROOT}/houston-industrial.jpg`,
    // One Multi-Family back cover for both offices - the client confirmed on 2026-09-29 that
    // Houston uses the same cover and back cover as Dallas. Sharing it costs nothing: the
    // office address on the strip is drawn live from OFFICE_ADDRESS (see BackCover.js), so a
    // Houston deck still prints the Houston office. Built from the already-patched Dallas
    // office file with its photo grid replaced, so the strip needs no patching again.
    //
    // `back-covers/houston-multi-family.jpg` is in the bucket but no longer referenced.
    'apartments':                   `${BACK_COVER_ROOT}/dallas-multi-family.jpg`,
};

/**
 * The office address printed on the contact strip, keyed by `officeLocation`.
 *
 * The back cover has this baked into its artwork, which is why there are eight back covers
 * for two offices. The cover cannot: its five files are shared between Houston and Dallas,
 * keyed by product and tenancy, so an address in the artwork would mean ten covers. Page 01
 * therefore draws the strip live from here - see `pages/Cover.js`.
 *
 * Transcribed from the back-cover artwork, which is the authority. The line breaks differ:
 * the artwork sets Houston over three lines because its cell is narrower than the space the
 * cover gives it.
 */
export const OFFICE_ADDRESS = {
    Dallas: ['17742 Preston Rd.', 'Dallas, TX 75252'],
    Houston: ['2603 Augusta Dr, Ste 1525', 'Houston, TX 77057'],
};

/** The domain cell. The same on every deck, so it is not per office. */
export const STRIVE_DOMAIN = 'STRIVEre.com';

function slug(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-');
}

export function templateKey(officeLocation, productType, subProductType) {
    return [slug(officeLocation), slug(productType), slug(subProductType)].join('/');
}

/**
 * Combinations we have to cover. Only the ones marked `implemented` render with the
 * new template; the rest fall through to the existing deck.
 */
export const BOV_TEMPLATES = {
    'houston/retail/multi-tenant': {
        implemented: true,
        officeLocation: 'Houston',
        productType: 'Retail',
        subProductType: 'Multi Tenant',
        tenancy: 'multi',
        totalPages: 11,
        cover: COVER['retail/multi-tenant'],
        backCover: BACK_COVER['houston/retail/multi-tenant'],
        // Global Reach is the only page with no live data on it. Page 06 on this deck.
        // The page 08 skyline is the only artwork here that varies by office location.
        capitalMap: SHARED_CAPITAL_MAP,
        cityImage: CITY_HOUSTON,
    },

    // The single-tenant deck is the multi-tenant one with page 02 rebuilt and the rent
    // roll dropped, so it is nine pages rather than ten and every page from Capital Flows
    // on is numbered one lower. The marketing pages themselves are identical, and their
    // artwork is byte-identical in the two source PDFs - the files below are copies of
    // the multi-tenant ones.
    'houston/retail/single-tenant': {
        implemented: true,
        officeLocation: 'Houston',
        productType: 'Retail',
        subProductType: 'Single Tenant',
        tenancy: 'single',
        totalPages: 10,
        cover: COVER['retail/single-tenant'],
        backCover: BACK_COVER['houston/retail/single-tenant'],
        // Global Reach again, page 05 on a single-tenant deck rather than 06.
        capitalMap: SHARED_CAPITAL_MAP,
        cityImage: CITY_HOUSTON,
    },

    // Identical to the Houston multi-tenant deck in every respect except the page 08
    // skyline: the two source PDFs have zero text difference and every other embedded
    // image, the cover included, is byte-identical. The files below other than
    // `city.jpg` are copies of the Houston ones.
    'dallas/retail/multi-tenant': {
        implemented: true,
        officeLocation: 'Dallas',
        productType: 'Retail',
        subProductType: 'Multi Tenant',
        tenancy: 'multi',
        totalPages: 11,
        cover: COVER['retail/multi-tenant'],
        backCover: BACK_COVER['dallas/retail/multi-tenant'],
        capitalMap: SHARED_CAPITAL_MAP,
        cityImage: CITY_DALLAS,
        // The Dallas skyline is a wider, shorter drawing than the Houston one. The
        // template places both on the same left and bottom edge at the same width and
        // lets the height follow, so this one sits 16px shorter.
        cityImageHeight: CITY_DALLAS_HEIGHT,
    },

    // Same relationship again: identical to the Houston single-tenant deck but for the
    // page 07 skyline, with zero text difference and every other embedded image
    // byte-identical. The skyline is the same stream as the Dallas multi-tenant deck's,
    // so `city.jpg` here is a copy of that folder's processed file; the rest are copies
    // of the Houston single-tenant ones.
    'dallas/retail/single-tenant': {
        implemented: true,
        officeLocation: 'Dallas',
        productType: 'Retail',
        subProductType: 'Single Tenant',
        tenancy: 'single',
        totalPages: 10,
        cover: COVER['retail/single-tenant'],
        backCover: BACK_COVER['dallas/retail/single-tenant'],
        capitalMap: SHARED_CAPITAL_MAP,
        cityImage: CITY_DALLAS,
        cityImageHeight: CITY_DALLAS_HEIGHT,
    },

    // Houston / Office / Multi Tenant. The reference for this one is a set of page JPGs
    // rather than a PDF, so there is no text layer or embedded artwork to extract: the
    // cover is the supplied page 01 rescaled, and the marketing artwork is copied from the
    // Houston retail multi-tenant folder, whose pages 05, 06 and 08 the office pages match
    // in design.
    //
    // Do not read text differences off those JPGs as design changes. They were exported on
    // a machine missing the template's fonts, so labels are substituted and in places
    // truncated - page 06's "INVESTORS BY REGION" panel loses two labels and three
    // percentages outright. The retail PDF is the reliable source for every shared page.
    'houston/office/multi-tenant': {
        implemented: true,
        officeLocation: 'Houston',
        productType: 'Office',
        subProductType: 'Multi Tenant',
        tenancy: 'multi',
        totalPages: 11,
        // The eyebrow reads "OFFERING SUMMARY  •  OFFICE" on this deck, where the retail
        // ones read "•  MULTI TENANT".
        cover: COVER['office/multi-tenant'],
        backCover: BACK_COVER['houston/office'],
        capitalMap: SHARED_CAPITAL_MAP,
        cityImage: CITY_HOUSTON,
    },

    // The retail single-tenant deck with a different cover and "OFFICE" in the eyebrow, and
    // nothing else changed. Checked page by page against the retail single-tenant reference:
    // only pages 01 and 02-04 differ, 01 being the cover and 02-04 only the eyebrow suffix.
    // The boilerplate on page 03 still reads "Retail Team" and page 04 still reads "multi
    // tenant retail" on the approved office template, so the copy is deliberately shared.
    // `city.jpg` is a copy of the retail single-tenant file, which page 07 renders
    // identically to.
    'houston/office/single-tenant': {
        implemented: true,
        officeLocation: 'Houston',
        productType: 'Office',
        subProductType: 'Single Tenant',
        tenancy: 'single',
        totalPages: 10,
        cover: COVER['office/single-tenant'],
        backCover: BACK_COVER['houston/office'],
        capitalMap: SHARED_CAPITAL_MAP,
        cityImage: CITY_HOUSTON,
    },

    // The Houston office deck with the page 08 skyline swapped for the Dallas one - the same
    // relationship the two retail multi-tenant decks have. Eight of the ten reference JPGs
    // are byte-identical files; only the cover and page 08 differ, and the cover difference
    // is JPEG re-encoding rather than different artwork (the difference sits on edges, at
    // 26 levels against 0.8 on flat areas, and no tile changed). So the cover here is a copy
    // of the Houston office one, already page-number-painted, and `city.jpg` is a copy of
    // the Dallas retail folder's processed skyline.
    'dallas/office/multi-tenant': {
        implemented: true,
        officeLocation: 'Dallas',
        productType: 'Office',
        subProductType: 'Multi Tenant',
        tenancy: 'multi',
        totalPages: 11,
        cover: COVER['office/multi-tenant'],
        backCover: BACK_COVER['dallas/office'],
        capitalMap: SHARED_CAPITAL_MAP,
        cityImage: CITY_DALLAS,
        cityImageHeight: CITY_DALLAS_HEIGHT,
    },

    // The Houston office single-tenant deck with the page 07 skyline swapped for the Dallas
    // one, and nothing else. Seven of the nine reference JPGs are byte-identical files; only
    // 01 and 07 differ. The page 07 skyline is the same drawing the Dallas retail decks use,
    // so `city.jpg` is a copy of that processed file. The cover difference is a re-export at
    // a slightly different scale rather than new artwork - amplifying the difference gives
    // pure edge outline with flat areas black, and no tile in the 4x4 grid changed - so the
    // cover is a copy of the Houston office single-tenant one, already page-number-painted.
    'dallas/office/single-tenant': {
        implemented: true,
        officeLocation: 'Dallas',
        productType: 'Office',
        subProductType: 'Single Tenant',
        tenancy: 'single',
        totalPages: 10,
        cover: COVER['office/single-tenant'],
        backCover: BACK_COVER['dallas/office'],
        capitalMap: SHARED_CAPITAL_MAP,
        cityImage: CITY_DALLAS,
        cityImageHeight: CITY_DALLAS_HEIGHT,
    },

    // The Dallas office deck with a new cover and one word changed. Pages 06-10 are
    // byte-identical reference files, and on pages 02-05 the only difference is the eyebrow
    // suffix - a 50x6px region in the same spot on each, reading INDUSTRIAL instead of
    // OFFICE. The cover is genuinely new artwork (a 4x4 grid of sixteen industrial
    // properties); everything else is a copy of the Dallas office folder.
    'dallas/industrial/multi-tenant': {
        implemented: true,
        officeLocation: 'Dallas',
        productType: 'Industrial',
        subProductType: 'Multi Tenant',
        tenancy: 'multi',
        totalPages: 11,
        cover: COVER['industrial/multi-tenant'],
        backCover: BACK_COVER['dallas/industrial'],
        capitalMap: SHARED_CAPITAL_MAP,
        cityImage: CITY_DALLAS,
        cityImageHeight: CITY_DALLAS_HEIGHT,
    },

    // The Dallas industrial deck with the Houston skyline. Nine of the ten reference JPGs are
    // byte-identical files, the cover included - covers vary by product type, not by city -
    // and page 08's difference is confined to the skyline box. Page 05's files differ too but
    // the images do not (mean difference 0.00, max 3 levels): encoding metadata only.
    //
    // No `cityImageHeight`: the Houston skyline is the taller drawing, and its height is the
    // one baked into PrintBOV.scss.
    'houston/industrial/multi-tenant': {
        implemented: true,
        officeLocation: 'Houston',
        productType: 'Industrial',
        subProductType: 'Multi Tenant',
        tenancy: 'multi',
        totalPages: 11,
        cover: COVER['industrial/multi-tenant'],
        backCover: BACK_COVER['houston/industrial'],
        capitalMap: SHARED_CAPITAL_MAP,
        cityImage: CITY_HOUSTON,
    },

    // The Houston office single-tenant deck with a new cover and "INDUSTRIAL" in the eyebrow.
    // Localised the differences with a grid diff rather than a whole-page one: pages 05, 06,
    // 08 and 09 are byte-identical reference files, pages 02-04 differ only in the eyebrow
    // cell with every other cell at 0.0, and page 07 differs only across the skyline block
    // and only by 0.6-2.5 levels - re-encoding of the same Houston drawing, not another city
    // (a real city change measures around 15.8 there). So only the cover is new artwork.
    'houston/industrial/single-tenant': {
        implemented: true,
        officeLocation: 'Houston',
        productType: 'Industrial',
        subProductType: 'Single Tenant',
        tenancy: 'single',
        totalPages: 10,
        cover: COVER['industrial/single-tenant'],
        backCover: BACK_COVER['houston/industrial'],
        capitalMap: SHARED_CAPITAL_MAP,
        cityImage: CITY_HOUSTON,
    },

    // The Houston industrial single-tenant deck with the page 07 skyline swapped for the
    // Dallas one. The plainest relationship in the set: the cover is a byte-identical source
    // file, pages 03, 05, 06, 08 and 09 are byte-identical too, and page 07 is byte-identical
    // to the Dallas office single-tenant page 07 - the same Dallas skyline.
    //
    // Pages 02 and 04 differ by md5 but not by image: page 04 measures 0.0 in every cell of a
    // grid diff, and page 02's largest single-pixel difference across the whole page is 5
    // levels out of 255, which is an encoding-quality change (it is 41KB larger), not content.
    // Neither needed anything.
    'dallas/industrial/single-tenant': {
        implemented: true,
        officeLocation: 'Dallas',
        productType: 'Industrial',
        subProductType: 'Single Tenant',
        tenancy: 'single',
        totalPages: 10,
        cover: COVER['industrial/single-tenant'],
        backCover: BACK_COVER['dallas/industrial'],
        capitalMap: SHARED_CAPITAL_MAP,
        cityImage: CITY_DALLAS,
        cityImageHeight: CITY_DALLAS_HEIGHT,
    },

    // Dallas / Multi-Family, from the "DALLAS - Multi Family - Proposal Template" page JPGs.
    //
    // Keyed `apartments`, not `multi-family`: that is the category code property-categories.js
    // stores for Multi-Family, and UnderwritingMultiTenant passes the raw code to getTemplate.
    // Only a multi-tenant key exists because Multi-Family has no Property Type - PropertyInfo
    // hides the dropdown for apartments and Financials therefore routes every one of them to
    // the multi-tenant screen. A property that was typed STNL *before* being switched to
    // Multi-Family keeps that type and still reaches the single-tenant screen, which asks for
    // `.../apartments/single-tenant`, finds nothing, and prints the old deck.
    //
    // `layout: 'multi-family'` swaps pages 02 and 03 for the Multi-Family ones - an apartment
    // deal has a unit mix, not a rent roll - and page 05 quotes Multi-Family closings rather
    // than multi-tenant ones. Every other page is the multi-tenant deck's.
    'dallas/apartments/multi-tenant': {
        implemented: true,
        officeLocation: 'Dallas',
        productType: 'Multi-Family',
        subProductType: 'Multi Tenant',
        eyebrowLabel: 'MULTI FAMILY',
        layout: 'multi-family',
        tenancy: 'multi',
        totalPages: 11,
        cover: COVER['apartments'],
        backCover: BACK_COVER['apartments'],
        capitalMap: SHARED_CAPITAL_MAP,
        cityImage: CITY_DALLAS,
        cityImageHeight: CITY_DALLAS_HEIGHT,
    },

    // The Dallas Multi-Family deck with the Houston skyline on page 08. Same cover and back
    // cover as Dallas, confirmed by the client 2026-09-29; the back cover's office address is
    // drawn live, so it prints Houston's. Which of the two decks a property gets is decided by
    // its ZIP through bovOfficeFor, exactly as for the other product types.
    'houston/apartments/multi-tenant': {
        implemented: true,
        officeLocation: 'Houston',
        productType: 'Multi-Family',
        subProductType: 'Multi Tenant',
        eyebrowLabel: 'MULTI FAMILY',
        layout: 'multi-family',
        tenancy: 'multi',
        totalPages: 11,
        cover: COVER['apartments'],
        backCover: BACK_COVER['apartments'],
        capitalMap: SHARED_CAPITAL_MAP,
        cityImage: CITY_HOUSTON,
    },
};

/**
 * The markets that resolve to an office on their own. This is the fallback for a property
 * whose ZIP is missing or malformed, not the primary answer - see `bovOfficeFor`.
 *
 * Only these three are here, and the three that are absent are the point: West Texas, East
 * Texas and Central Texas each straddle the client's dividing line, so naming an office for
 * them would be a guess. Austin and San Antonio are south of the line but are not in the
 * territory map at all, so a property in either carries whatever market was typed by hand.
 */
const MARKET_TO_OFFICE = {
    'dfw': 'Dallas',
    'houston': 'Houston',
    'valley': 'Houston',
};

/**
 * The BOV office covering a property - the office whose deck it prints, or null for a
 * property outside the client's split, which keeps the existing deck.
 *
 * ZIP decides it. The client splits every Texas ZIP at latitude 31.65N and half the Texas
 * markets cross that line, so market cannot answer this for a property in West, East or
 * Central Texas. Where a property has both, the two never disagree - every DFW-market ZIP in
 * the client's file is Dallas, every Houston-market and Valley ZIP is Houston - so reading
 * ZIP first changes no deck that prints today; it only starts answering for the markets that
 * have had no answer.
 *
 * Market remains as a fallback because ZIP is not guaranteed: it is required only for senior
 * agents, disabled for agents, and validated nowhere. Since the two sources never disagree,
 * the fallback can only rescue a property whose ZIP is blank or mistyped - it can never
 * change an office out from under a ZIP that resolves.
 */
/**
 * Where a property lands when neither its ZIP nor its market says.
 *
 * The client's file is Texas only - 2,664 ZIPs between 73301 and 88595 - so a property
 * anywhere else has never been in it. Those used to resolve to no office and print the old
 * deck; the business asked on 2026-09-26 that they print the Dallas one instead, after a
 * Florida property (33411, West Palm Beach) came through on the old template.
 *
 * Read what this does before changing it: with a default here, **the office is never
 * unknown**, so the old deck is no longer reachable by way of the office at all. It now
 * prints only where `getTemplate` finds no template for the product - "other", or a property
 * whose category was never filled in. Every retail, office, industrial and multi-family
 * property in the system gets the new deck, including the California, Louisiana, Oklahoma and
 * Arkansas markets, which will carry Dallas artwork and the Dallas office address on the back
 * cover.
 */
const DEFAULT_OFFICE = 'Dallas';

export function bovOfficeFor({zip, market}) {
    // Slice rather than match: a ZIP+4 is stored here as typed, so "75201-1234" and "75201"
    // are the same property. Anything that is not five digits falls through to the market.
    const zip5 = String(zip || '').trim().slice(0, 5);

    return BOV_ZIP_OFFICE[zip5] || MARKET_TO_OFFICE[slug(market)] || DEFAULT_OFFICE;
}

export function getTemplate(officeLocation, productType, subProductType) {
    const template = BOV_TEMPLATES[templateKey(officeLocation, productType, subProductType)];
    return template && template.implemented ? template : null;
}

export function isMultiFamily(template) {
    return !!template && template.layout === 'multi-family';
}

export function isSingleTenant(template) {
    return !!template && template.tenancy === 'single';
}

/**
 * The eyebrow suffix the template prints on pages 02, 03 and 04 - "OFFERING SUMMARY  •
 * SINGLE TENANT", "PRICING MATRIX  •  MULTI TENANT" and so on.
 *
 * It names the tenancy - what the business calls the property's *type* - and never the product
 * category, on every deck. The approved retail PDFs print "MULTI TENANT" and "SINGLE TENANT" on
 * both pages; the later per-product sheets appear to contradict that, but they carry "OFFICE" on
 * every export regardless of product or tenancy, so they are not evidence about this field.
 *
 * Office and industrial decks briefly printed their product category here instead. If a deck
 * ever needs its own wording again, add the override back rather than special-casing callers -
 * both pages read this one function.
 */
export function subProductLabel(template) {
    // Multi-Family is the one override: it has no tenancy, and its reference prints
    // "OFFERING SUMMARY  •  MULTI FAMILY".
    if (template && template.eyebrowLabel) return template.eyebrowLabel;
    return String((template && template.subProductType) || '').toUpperCase();
}

export function collectPreloadUrls(template) {
    if (!template) return [];
    // The Global Reach map is listed flat rather than read off the template: it is shared by
    // every deck, the same way BOV_LOGO is, and no longer hangs off a per-template entry now
    // that the page is markup.
    return [
        BOV_LOGO,
        GLOBAL_REACH_MAP,
        template.cover,
        template.backCover,
        template.capitalMap,
        template.cityImage,
    ].filter(Boolean);
}
